import React from 'react'
import { Link } from 'react-router-dom'
import { FaFish } from 'react-icons/fa'

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Navbar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <FaFish className="h-8 w-8 text-blue-600" />
              <span className="font-bold text-xl text-gray-900">AnglersAI</span>
            </Link>
          </div>
        </div>
      </nav>
      
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <Link to="/" className="text-blue-600 hover:text-blue-800 mb-8 inline-block">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
          <section>
            <p className="text-gray-600 mb-6">
              Last updated: {new Date().toLocaleDateString()}
            </p>
            
            <p className="text-gray-700 mb-6">
              Wright Media, LLC ("we," "our," or "us") operates the AnglersAI mobile application (the "Service"). 
              This page informs you of our policies regarding the collection, use, and disclosure of personal data 
              when you use our Service and the choices you have associated with that data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Collection and Use</h2>
            <p className="text-gray-700 mb-4">
              We collect several different types of information for various purposes to provide and improve our Service to you.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Types of Data Collected</h3>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Personal Data</h4>
            <p className="text-gray-700 mb-4">
              While using our Service, we may ask you to provide us with certain personally identifiable information 
              that can be used to contact or identify you ("Personal Data"). This may include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Device information</li>
              <li>Usage data</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Usage Data</h4>
            <p className="text-gray-700 mb-4">
              We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data 
              may include information such as your device's Internet Protocol address, device type, operating system, 
              the pages of our Service that you visit, the time and date of your visit, and other diagnostic data.
            </p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Camera and Photos</h4>
            <p className="text-gray-700 mb-4">
              Our app requires access to your device's camera to measure fish. Photos taken within the app are processed 
              locally on your device for measurement purposes. Photos are only uploaded to our servers if you explicitly 
              choose to save them to your cloud storage as part of our premium features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use of Data</h2>
            <p className="text-gray-700 mb-4">AnglersAI uses the collected data for various purposes:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>To provide and maintain our Service</li>
              <li>To notify you about changes to our Service</li>
              <li>To provide customer support</li>
              <li>To gather analysis or valuable information so that we can improve our Service</li>
              <li>To monitor the usage of our Service</li>
              <li>To detect, prevent and address technical issues</li>
              <li>To provide you with news and general information about our service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Storage and Security</h2>
            <p className="text-gray-700 mb-4">
              We use Cloudflare services to store and process your data. Your data is encrypted both in transit and at rest. 
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized 
              or unlawful processing and against accidental loss, destruction, or damage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Subscription Information</h2>
            <p className="text-gray-700 mb-4">
              If you purchase a subscription through our app, the transaction is processed by Apple's App Store. 
              We do not store or have access to your payment information. We only receive confirmation of your subscription 
              status from Apple.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              We may employ third-party companies and individuals to facilitate our Service ("Service Providers"). 
              These third parties have access to your Personal Data only to perform these tasks on our behalf and 
              are obligated not to disclose or use it for any other purpose.
            </p>
            <p className="text-gray-700 mb-4">We use the following third-party services:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Apple App Store for payment processing</li>
              <li>Cloudflare for data storage and API services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our Service does not address anyone under the age of 13. We do not knowingly collect personally 
              identifiable information from anyone under the age of 13. If you are a parent or guardian and you are 
              aware that your child has provided us with Personal Data, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Data Protection Rights</h2>
            <p className="text-gray-700 mb-4">You have certain data protection rights. You have the right to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Access, update or delete your personal information</li>
              <li>Object to processing of your personal data</li>
              <li>Request restriction of processing your personal data</li>
              <li>Request portability of your personal data</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise any of these rights, please contact us at the email address below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the 
              new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>By email: support@wrightmedia.com</li>
              <li>By visiting our website: www.wrightmedia.com</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy